// Level3.js
import React, { useState, useEffect, useRef } from "react";
import check from "../../assets/check.png";
import more from "../../assets/more.svg";
import { useDispatch, useSelector } from "react-redux";
import { updateLevel3, updateLevel6 } from "../../store/QuestionnaireSlice";
import editPen from "../../assets/edit_pen.svg";
import trash from "../../assets/trash.svg";
import { forwardRef } from "react";
import { useImperativeHandle } from "react";

const Level3 = forwardRef(
  ({ onNext, onBack, placement, editError, setEditError }, ref) => {
    const dispatch = useDispatch();
    const functionalUnits = useSelector(
      (state) => state.questionnaire.level3Data
    );
    const level6data = useSelector((state) => state.questionnaire.level6Data);
    const questions = useSelector((state) => state.questionnaire.questions);
    const inputRef = useRef(null);

    const [functionalUnitTitle, setFunctionalUnitTitle] = useState("");
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [editingIndex, setEditingIndex] = useState(null);
    const [editFunctionTitle, setEditFunctionTitle] = useState("");
    const [error, setError] = useState("");

    useImperativeHandle(ref, () => ({
      handleNextClick,
    }));

    const handleAddFunctionalUnit = () => {
      if (functionalUnitTitle.trim()) {
        dispatch(
          updateLevel3([
            ...functionalUnits,
            { _id: "", name: functionalUnitTitle },
          ])
        );
        setFunctionalUnitTitle("");
      }
      inputRef.current?.focus();
    };

    const handleDeleteUnit = (index) => {
      const updatedUnits = functionalUnits.filter((_, i) => i !== index);
      let updatedFunctionalDepartment = { ...level6data };
      delete updatedFunctionalDepartment[functionalUnits[index].name];
      dispatch(updateLevel3(updatedUnits));
      dispatch(updateLevel6(updatedFunctionalDepartment));
      setActiveDropdown(null);
    };

    const handleEditUnit = (index) => {
      setEditingIndex(index);
      setEditFunctionTitle(functionalUnits[index].name);
      setActiveDropdown(null);
    };

    const handleUpdateUnit = (index, newValue) => {
      if (newValue.trim()) {
        const updatedUnits = [...functionalUnits];
        updatedUnits[index] = { ...updatedUnits[index], name: newValue };
        let updatedLevel6 = { ...level6data };
        updatedLevel6 = {
          ...updatedLevel6,
          [newValue]: updatedLevel6[functionalUnits[index].name],
        };
        if (newValue !== functionalUnits[index].name) {
          delete updatedLevel6[functionalUnits[index].name];
        }

        dispatch(updateLevel3(updatedUnits));
        dispatch(updateLevel6(updatedLevel6));
        setEditingIndex(null);
      } else {
        setError("Functional unit title cannot be empty");
        setEditingIndex(null);
        return "Functional unit title cannot be empty";
      }
    };

    const handleNextClick = () => {
      if (functionalUnitTitle) {
        if (functionalUnitTitle.trim()) {
          dispatch(
            updateLevel3([
              ...functionalUnits,
              { _id: "", name: functionalUnitTitle },
            ])
          );
          setFunctionalUnitTitle("");
        }
      }
      if (functionalUnits.length === 0 && !functionalUnitTitle) {
        setError("Please add at least one functional unit");
        return "Please add at least one functional unit";
      }
      for (let i = 0; i < functionalUnits.length; i++) {
        if (!functionalUnits[i].name.trim()) {
          setError("Functional unit title cannot be empty");
          return "Functional unit title cannot be empty";
        }
      }
      if (onNext) {
        onNext();
      }
    };

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = () => setActiveDropdown(null);
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }, []);

    return (
      <div>
        <div
          className={` pb-6 px-10 mx-auto rounded-lg max-w-[1200px] ${
            placement !== "edit" ? "bg-[#F4F5F6] pt-[30px]" : "bg-white"
          }`}
        >
          <div className="bg-[#1C3F3A] text-white px-[10px] py-[6px] w-fit rounded-md text-[12px]">
            Level {questions[2]?.level || "3"}
          </div>
          <p
            className={`mt-3 mb-6 font-medium ${
              placement === "edit" ? "text-[1.125rem]" : "text-[1.25rem]"
            }`}
          >
            {questions[2]?.description ||
              "Please indicate the functional units around which your firm is organized"}
          </p>
          {(error || editError) && (
            <p className="flex justify-between items-center bg-red-100 border-l-2 border-0 mb-5 border-red-400 text-red-700 px-4 py-3">
              {error ? error : editError}
              <span
                className="cursor-pointer"
                onClick={() => {
                  if (editError) {
                    setEditError("");
                  } else {
                    setError("");
                  }
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 26 26"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </span>
            </p>
          )}
          <div className="">
            <div className="flex flex-wrap gap-2.5 gap-y-5 mb-4">
              {functionalUnits.map((functionalUnit, index) =>
                editingIndex === index ? (
                  <div className="max-w-[210px]">
                    <input
                      type="text"
                      value={editFunctionTitle}
                      onChange={(e) => setEditFunctionTitle(e.target.value)}
                      onBlur={() => handleUpdateUnit(index, editFunctionTitle)}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          handleUpdateUnit(index, editFunctionTitle);
                        }
                      }}
                      className="px-[0.75rem] py-[0.325rem] w-full border border-[#D9D9D9] rounded-lg focus:outline-none"
                      autoFocus
                    />
                  </div>
                ) : (
                  <div
                    key={index}
                    className="flex items-center justify-between gap-3 h-fit px-[0.75rem] py-[0.325rem] min-w-[210px] bg-white rounded-lg shadow-sm border border-[#D9D9D9]"
                    onClick={() => handleEditUnit(index)}
                  >
                    <span className="font-[500]">{functionalUnit.name}</span>
                    <div className="relative flex">
                      <button
                        className="text-gray-400"
                        onClick={(e) => {
                          e.stopPropagation();
                          // setActiveDropdown(
                          //   activeDropdown === index ? null : index
                          // );
                          handleDeleteUnit(index);
                        }}
                      >
                        <img src={trash} alt="" className="" />
                      </button>
                      {/* {activeDropdown === index && (
                        <div className="absolute right-0 mt-2 w-32 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                          <button
                            className="w-full px-4 py-2 text-left text-sm border-b border-[#D9D9D9] hover:bg-gray-100 flex items-center gap-2"
                            onClick={() => handleEditUnit(index)}
                          >
                            <img src={editPen} alt="" className="" />
                            Edit
                          </button>
                          <button
                            className="w-full px-4 py-2 text-left hover:bg-gray-100 text-sm flex items-center gap-2"
                            onClick={() => handleDeleteUnit(index)}
                          >
                            <img src={trash} alt="" className="" />
                            Delete
                          </button>
                        </div>
                      )} */}
                    </div>
                  </div>
                )
              )}

              <div className="relative w-[210px]">
                <input
                  ref={inputRef}
                  type="text"
                  value={functionalUnitTitle}
                  onChange={(e) => setFunctionalUnitTitle(e.target.value)}
                  placeholder="Enter Function Title"
                  className="px-4 py-[0.325rem] w-full border border-[#D9D9D9] rounded-[6px] focus:outline-none"
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleAddFunctionalUnit();
                    }
                  }}
                  autoFocus
                />
                <img
                  src={check}
                  alt=""
                  className="absolute right-3 top-3 max-sm:hidden"
                />
              </div>

              <button
                onClick={handleAddFunctionalUnit}
                className="relative py-[0.375rem] w-[210px] h-fit font-medium rounded-[6px] text-[#1C3F3A] hover:bg-[#f0f0f0] transition-colors"
              >
                <svg
                  width="100%"
                  height="100%"
                  viewBox="0 0 210 36"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="absolute inset-0"
                  preserveAspectRatio="none"
                >
                  <rect
                    x="0.5"
                    y="0.5"
                    width="99%"
                    height="97%"
                    rx="5.5"
                    fill="white"
                    stroke="#1C3F3A"
                    strokeWidth="1"
                    strokeDasharray="4 4"
                  />
                </svg>
                <span className="relative z-10">Add New Function</span>
              </button>
            </div>
          </div>
        </div>
        {placement !== "edit" && (
          <div className="flex justify-between mt-[26px]">
            <button
              className="border border-[#D9D9D9] text-[#1C3F3A] flex gap-3 justify-center items-center py-[0.825rem] rounded-[10px] font-inter font-medium px-5 hover:bg-[#f0f0f0] transition-colors"
              onClick={onBack}
            >
              <svg
                width="17"
                height="8"
                viewBox="0 0 17 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3.80775 0.192239L4.52125 0.899989L1.92125 3.49999H17V4.49999H1.92125L4.5155 7.09999L3.802 7.80774L0 3.99999L3.80775 0.192239Z"
                  fill="#1C3F3A"
                />
              </svg>
              Back
            </button>
            <button
              className="bg-[#55C880] flex items-center justify-center gap-3 text-white pl-5 pr-6 py-[0.825rem] rounded-[10px] overflow-hidden"
              onClick={handleNextClick}
            >
              Next
              <svg
                width="17"
                height="8"
                viewBox="0 0 17 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.1923 7.80776L12.4788 7.10001L15.0788 4.50001H0V3.50001H15.0788L12.4845 0.900011L13.198 0.192261L17 4.00001L13.1923 7.80776Z"
                  fill="white"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
    );
  }
);

export default Level3;
